'use client'

import {
  CompanyLocationSearch,
  LocationAddressDisplay,
  type LocationPlaceValue,
  mapCompanyLocationToPlaceValue,
} from '@repo/features/location'
import {
  type Coordinates,
  type GoogleLocationResult,
  type PastedLocationResult,
} from '@repo/geolocation'
import { CompanyLocation } from '@repo/schema/location'
import {
  Button,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@repo/ui/components'
import { Pencil } from '@repo/ui/icons'
import { ReactNode, type Ref } from 'react'

import { ReadOnlyTooltip, useReadOnly } from '../../components/readonly.tsx'
import { useFieldContext } from '../../lib/form-context.ts'

type CompanyLocationInputProps = {
  className?: string
  companyId?: null | number
  description?: ReactNode | string
  disabled?: boolean
  isCreatingLocation?: boolean
  label?: ReactNode | string
  nearbyCoordinates?: Coordinates
  onAddNewLocation?: (context: { googlePlace?: GoogleLocationResult; query: string }) => void
  onEditLocation?: (location: LocationPlaceValue) => void
  onGoogleLocationSelect?: (location: GoogleLocationResult) => void
  onPastedLocationSelect?: (location: PastedLocationResult) => void
  orgIdOverride?: number
  placeholder?: string
  restoreFocusOnSelect?: boolean
  showAddress?: boolean
  triggerRef?: Ref<HTMLButtonElement>
}

export const CompanyLocationInput = ({
  className,
  companyId,
  description,
  disabled,
  isCreatingLocation,
  label,
  nearbyCoordinates,
  onAddNewLocation,
  onEditLocation,
  onGoogleLocationSelect,
  onPastedLocationSelect,
  orgIdOverride,
  placeholder,
  restoreFocusOnSelect = false,
  showAddress = true,
  triggerRef,
}: CompanyLocationInputProps) => {
  const field = useFieldContext<LocationPlaceValue | null | undefined>()
  const isReadOnly = useReadOnly()
  const errors = field.state.meta.errors ?? []
  const isInvalid = errors.length > 0
  const value = field.state.value

  const handleCompanyLocationSelect = (location: CompanyLocation) => {
    field.handleChange(mapCompanyLocationToPlaceValue(location))
  }

  return (
    <Field className="gap-1" data-invalid={isInvalid} data-sentry-element="Field" data-sentry-component="CompanyLocationInput" data-sentry-source-file="company-location-input.tsx">
      {label ? (
        <div className="flex items-center gap-2">
          {typeof label === 'string' ? <FieldLabel>{label}</FieldLabel> : label}
          {value?.location_id != null && onEditLocation && !isReadOnly && (
            <Button
              aria-label="Edit location"
              // Negative margin keeps the label row as tall as a row without the button.
              className="-my-0.5 size-6"
              disabled={disabled}
              onClick={() => onEditLocation(value)}
              size="icon-xs"
              type="button"
              variant="ghost"
            >
              <Pencil className="size-3.5" />
            </Button>
          )}
        </div>
      ) : null}
      <FieldContent className="min-w-0" data-sentry-element="FieldContent" data-sentry-source-file="company-location-input.tsx">
        {isReadOnly ? (
          <ReadOnlyTooltip>{value?.name || value?.place_name || '-'}</ReadOnlyTooltip>
        ) : (
          <CompanyLocationSearch
            className={className}
            companyId={companyId}
            disabled={disabled}
            invalid={isInvalid}
            isCreatingLocation={isCreatingLocation}
            nearbyCoordinates={nearbyCoordinates}
            onAddNewLocation={context => {
              onAddNewLocation?.(context)
            }}
            onCompanyLocationSelect={handleCompanyLocationSelect}
            onGoogleLocationSelect={location => {
              onGoogleLocationSelect?.(location)
            }}
            onPastedLocationSelect={location => {
              onPastedLocationSelect?.(location)
            }}
            orgIdOverride={orgIdOverride}
            placeholder={placeholder}
            restoreFocusOnSelect={restoreFocusOnSelect}
            triggerRef={triggerRef}
            value={value ?? null}
          />
        )}
        {showAddress && value && (
          <LocationAddressDisplay className="text-muted-foreground text-sm" value={value} />
        )}
        {description && typeof description === 'string' ? (
          <FieldDescription>{description}</FieldDescription>
        ) : (
          description
        )}
        <FieldError className="text-sm" errors={errors} data-sentry-element="FieldError" data-sentry-source-file="company-location-input.tsx" />
      </FieldContent>
    </Field>
  )
}
